import { useMemo } from 'react'

import { resourceListTag, useOpenWindow, useWindow, useQueryResult } from '@krp/client-runtime'
import {
  ImportWizardScreen,
  ONEC_IMPORT_TAG,
  ONEC_IMPORT_WINDOWS,
  attemptKey,
  attemptRecorded,
  buildMappingArgs,
  columnRoleChanged,
  importApi,
  makeSelectWizardVm,
  mappingChanged,
  newCommandAttempt,
  onecImportParse,
  onecImportProfiles,
  onecConnectionForm,
  planUpload,
  uploadAttemptRecorded,
  uploadParamsChanged,
  type WizardActions,
} from '@krp/resource-onec-ui'

import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { useResourceRevision } from '../platform/useResourceRevision'

/**
 * Binding окна «Импорт накладной». Файл в Redux не попадает: он уходит из поля
 * ввода в реестр загрузок, в аргументе мутации — токен. Результат загрузки и
 * разметки держат две мутации с ключами `rid:…`, так что чужая база не видна.
 */
export function ImportWizardBinding() {
  const window = useWindow()
  const dispatch = useAppDispatch()
  const openWindow = useOpenWindow()
  const resourceId = window.params.rid ?? ''
  const intent = useAppSelector((state) => state.onec.importFlow)

  const profilesArgs = useMemo(
    () => (resourceId === '' ? null : { resourceId, includeInactive: false, supplier: null }),
    [resourceId],
  )
  const profiles = useQueryResult(onecImportProfiles, profilesArgs)
  const [uploadTrigger, upload] = onecImportParse.useMutation({ fixedCacheKey: `${resourceId}:import-upload` })
  const [mappingTrigger, mapping] = onecImportParse.useMutation({ fixedCacheKey: `${resourceId}:import-mapping` })

  const resourceVersion = useResourceRevision(resourceId, onecConnectionForm.kind, 'import.read')
  const selectVm = useMemo(() => makeSelectWizardVm(), [])
  const vm = selectVm({
    resourceId,
    intent,
    profiles,
    upload,
    mapping,
    resourceVersion,
  })

  const actions = useMemo<WizardActions>(
    () => ({
      paramsChanged: (patch) => dispatch(uploadParamsChanged({ resourceId, patch })),
      upload: (file) => {
        const plan = planUpload(vm, file, intent.uploadAttempts[resourceId])
        if (plan === null) return
        dispatch(uploadAttemptRecorded({ resourceId, fingerprint: plan.fingerprint, attempt: plan.attempt }))
        void uploadTrigger(plan.args)
      },
      roleChanged: (columnIndex, role) => {
        if (vm.parsed !== null) dispatch(columnRoleChanged({ resourceId, fileId: vm.parsed.fileId, columnIndex, role }))
      },
      mappingChanged: (patch) => {
        if (vm.parsed !== null) dispatch(mappingChanged({ resourceId, fileId: vm.parsed.fileId, patch }))
      },
      applyMapping: () => {
        if (vm.parsed === null) return
        const fileId = vm.parsed.fileId
        const key = attemptKey('mapping', resourceId, fileId)
        const attempt = intent.attempts[key] ?? newCommandAttempt()
        const args = buildMappingArgs(vm, vm.parsed.editor, fileId, attempt)
        if (args === null) return
        dispatch(attemptRecorded({ key, attempt }))
        void mappingTrigger(args)
      },
      openFile: () => {
        if (vm.parsed !== null) openWindow(ONEC_IMPORT_WINDOWS.file, { rid: resourceId, fileId: vm.parsed.fileId })
      },
      openProfiles: () => openWindow(ONEC_IMPORT_WINDOWS.profiles, { rid: resourceId }),
      reload: () => dispatch(importApi.util.invalidateTags([resourceListTag(ONEC_IMPORT_TAG, resourceId)])),
    }),
    [dispatch, openWindow, resourceId, vm, intent.uploadAttempts, intent.attempts, uploadTrigger, mappingTrigger],
  )

  return <ImportWizardScreen vm={vm} actions={actions} />
}
